<script setup lang="ts">
  import { KEY_ESCAPE, setBodyOverflow } from '@travelata-libs/utils';
  import { onKeyStroke, useSwipe } from '@vueuse/core';
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
  import Teleport from 'vue2-teleport'; // eslint-disable-line @typescript-eslint/no-unused-vars
  import { Typo } from '@/components';

  interface BottomSheetProps {
    height?: string;
    title?: string;
    overlayClosable?: boolean;
    swipeClosable?: boolean;
    zIndex?: number;
    contentClass?: string;
    rootClass?: string;
  }

  interface BottomSheetEmits {
    (e: 'close'): void;
  }

  const props = withDefaults(defineProps<BottomSheetProps>(), {
    height: 'auto',
    title: '',
    overlayClosable: true,
    swipeClosable: true,
    zIndex: 5,
    contentClass: '',
    rootClass: '',
  });

  const emit = defineEmits<BottomSheetEmits>();

  const overlayElement = ref<HTMLDivElement>();
  const lineElement = ref<HTMLDivElement>();
  const contentRef = ref<HTMLDivElement | null>(null);

  const bottom = ref('0');
  const opacity = ref(1);

  const overlayHeight = computed(() => overlayElement.value?.offsetHeight);

  const overlayStyle = computed(() => ({
    opacity: opacity.value,
    zIndex: props.zIndex,
  }));

  const floatingStyle = computed(() => ({
    height: props.height,
    bottom: bottom.value,
  }));

  const close = () => {
    emit('close');
  };

  const onOverlayClick = () => {
    if (!props.overlayClosable) {
      return;
    }
    close();
  };

  const { lengthY } = useSwipe(lineElement, {
    onSwipe() {
      if (lineElement.value && overlayHeight.value) {
        if (lengthY.value < 0) {
          const length = Math.abs(lengthY.value);
          bottom.value = `-${length}px`;
          opacity.value = 1.1 - (length * 2) / overlayHeight.value;
        } else {
          bottom.value = '0';
          opacity.value = 1;
        }
      }
    },
    onSwipeEnd() {
      if (lengthY.value < 0 && overlayHeight.value && Math.abs(lengthY.value) / overlayHeight.value >= 0.2) {
        bottom.value = '-100%';
        opacity.value = 0;
        setTimeout(close, 100);
      } else {
        bottom.value = '0';
        opacity.value = 1;
      }
    },
  });

  const resetScroll = () => {
    if (contentRef.value) {
      contentRef.value?.firstElementChild?.scrollIntoView({
        block: 'start',
        inline: 'nearest',
      });
    }
  };

  function onTouchMove(e: TouchEvent) {
    const target = e.target as HTMLElement;

    if (!contentRef.value?.contains(target)) {
      e.preventDefault();
    }
  }

  onKeyStroke(KEY_ESCAPE, (e) => {
    e.preventDefault();
    close();
  });

  onMounted(() => {
    setBodyOverflow('hidden');
    document.body.addEventListener('touchmove', onTouchMove, { passive: false });
  });

  onBeforeUnmount(() => {
    setBodyOverflow('visible');
    document.body.removeEventListener('touchmove', onTouchMove);
  });

  defineExpose({
    resetScroll,
  });
</script>

<template>
  <Teleport to="body">
    <div :class="[rootClass, 'tl-bottom-sheet']" data-test-id="bottom-sheet">
      <div
        ref="overlayElement"
        class="tl-bottom-sheet__overlay"
        :style="overlayStyle"
        data-test-id="overlay"
        @click.self="onOverlayClick"
      >
        <div :style="floatingStyle" class="tl-bottom-sheet__floating" data-test-id="floating">
          <div v-if="swipeClosable" ref="lineElement" class="tl-bottom-sheet__line-wrapper">
            <div class="tl-bottom-sheet__line" />
          </div>

          <header v-if="$slots.header || title" class="tl-bottom-sheet__header">
            <slot name="header">
              <Typo v-if="title" class="tl-bottom-sheet__title" variant="h-5" data-test-id="title">
                {{ title }}
              </Typo>
            </slot>
          </header>

          <div ref="contentRef" :class="[contentClass, 'tl-bottom-sheet__content']">
            <slot />
          </div>

          <footer v-if="$slots.footer" class="tl-bottom-sheet__footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
  .tl-bottom-sheet {
    &__overlay {
      align-items: center;
      background: var(--color-bg-modal);
      display: flex;
      flex-direction: column;
      height: 100%;
      inset: 0;
      justify-content: flex-end;
      position: fixed;
      width: 100%;
    }

    &__floating {
      background: var(--color-bg-primary);
      border-radius: var(--rounding-m) var(--rounding-m) 0 0;
      bottom: 0;
      box-shadow: var(--shadow-m);
      box-sizing: border-box;
      color: var(--color-text-primary);
      display: flex;
      flex-direction: column;
      left: 0;
      max-height: calc(100% - var(--spacing-xl));
      min-height: 150px;
      padding: calc(var(--spacing-xs) + var(--spacing-l)) 0 var(--spacing-l);
      position: absolute;
      width: 100%;
      z-index: 10;
    }

    &__header {
      align-items: center;
      background-color: inherit;
      display: flex;
      gap: var(--spacing-xs);
      justify-content: space-between;
      padding: 0 var(--spacing-l) var(--spacing-l);
      position: relative;
      z-index: 5;
    }

    &__line-wrapper {
      align-items: center;
      content: '';
      display: flex;
      justify-content: center;
      left: 0;
      padding: var(--spacing-m) 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &__line {
      background-color: var(--color-text-secondary);
      border-radius: var(--rounding-xs);
      height: 4px;
      width: 34px;
    }

    &__title {
      width: 100%;
    }

    &__content {
      color: var(--color-text-secondary);
      flex: 1;
      overflow: auto;
      padding: 0 var(--spacing-l);

      @include typography-body(l, 400);
    }

    &__footer {
      align-items: center;
      background-color: inherit;
      display: flex;
      gap: var(--spacing-xs);
      padding: var(--spacing-xl) var(--spacing-l) 0;
      position: relative;
      z-index: 5;
    }
  }
</style>
